import React, { useContext } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { AppDataContext } from "@src/components/AppDataContext";
import Button from "@src/components/Button";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";

type FollowButtonProps = { isOutline?: boolean; isSmall?: boolean; isPill?: boolean };
const FollowButton = styled.button<FollowButtonProps>`
  height: 24px;
  padding: 5px 8.5px;
  border-radius: 4px;
  font-family: proxima-nova, serif;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: 0;
  background-color: transparent;
  color: #00aeef;
  border: 1px solid #00aeef;
  cursor: pointer;

  &:hover {
    color: #00bdff;
    border-color: #00bdff;
  }

  &:focus {
    outline: none;
  }

  ${(props) =>
    props.isSmall &&
    css`
      font-size: 0.9em;
    `}

  ${(props) =>
    props.isOutline &&
    css`
      border: 1px solid #747474;
      color: #747474;
      padding-left: 26px;
      object-fit: contain;
      background-repeat: no-repeat;
      background-position: 5.5px 4.5px;
      background-size: 14px 14px;
      background-image: url(data:image/svg+xml;base64,PHN2ZyBmaWxsPSIjOGM4YzhjIiBoZWlnaHQ9IjQ4IiB2aWV3Qm94PSIwIDAgMjQgMjQiIHdpZHRoPSI0OCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4gICAgPHBhdGggZD0iTTAgMGgyNHYyNEgweiIgZmlsbD0ibm9uZSIvPiAgICA8cGF0aCBkPSJNOSAxNi4xN0w0LjgzIDEybC0xLjQyIDEuNDFMOSAxOSAyMSA3bC0xLjQxLTEuNDF6Ii8+PC9zdmc+);

      &:hover {
        border: 1px solid #747474;
        color: #747474;
      }
    `}
`;

type IdeasFollowType = { lesson?: any; text?: string; boardSlug?: string; bigButton?: boolean };
const IdeasFollow: React.FC<IdeasFollowType> = ({ lesson, text, boardSlug, bigButton }) => {
  // can't use default argument due to 'null'
  const appData = useContext(AppDataContext);

  const user = appData.data.userData;
  const follow = appData.data.services.followChannel;
  const unfollow = appData.data.services.unfollowChannel;

  let following = appData.data.followingChannels;
  if (!following) {
    following = [];
  }

  if (!user) {
    return <SignedOutFollow text={text} bigButton={bigButton} />;
  }

  const isTeacher = user && user.type === "teacher";
  const slug = lesson ? lesson.publisher.slug : boardSlug;
  // user is signed out
  if (!isTeacher) {
    return null;
  }

  // user is signed in and following channel
  const isFollowing = following.find(({ channelName }) => channelName === slug);

  if (isFollowing) {
    return <AlreadyFollow slug={slug} unfollow={unfollow} bigButton={bigButton} />;
  }

  return <SignedInFollow slug={slug} follow={follow} text={text} bigButton={bigButton} />;
};

function AlreadyFollow({ slug, unfollow, bigButton }) {
  if (bigButton)
    return (
      <Button outline onClick={() => unfollow(slug)}>
        Following
      </Button>
    );
  else
    return (
      <FollowButton type="button" isSmall isOutline onClick={() => unfollow(slug)}>
        Following
      </FollowButton>
    );
}

function SignedInFollow({ slug, follow, text, bigButton }) {
  if (bigButton) return <Button onClick={() => follow(slug)}>{text || "Follow"}</Button>;
  else
    return (
      <FollowButton type="button" isSmall onClick={() => follow(slug)}>
        {text || "Follow"}
      </FollowButton>
    );
}

const SignedOutFollow = ({ text, bigButton }) => {
  const modals = useContext(ModalContext);
  const showLoginModal = () => modals.showModal(ModalType.TeacherLogin, { form: { noRedirect: true } });

  return (
    <div>
      {bigButton ? (
        <Button onClick={showLoginModal}>{text || "Follow"}</Button>
      ) : (
        <FollowButton type="button" isSmall isPill onClick={showLoginModal}>
          {text || "Follow"}
        </FollowButton>
      )}
    </div>
  );
};

export default IdeasFollow;
